import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();
  
  // Define a default team array to use if translation doesn't exist or isn't an array
  const defaultTeam = [
    {
      name: 'Jacky',
      role: t('about.team.members.0.role', 'Founder & CEO'),
      bio: t('about.team.members.0.bio', 'Over 10 years of experience in cryptocurrency markets, having held executive positions in multiple top exchanges.'),
    },
    {
      name: 'Seven',
      role: t('about.team.members.1.role', 'CTO'),
      bio: t('about.team.members.1.bio', 'Former Google senior engineer, focusing on high-frequency trading system development and quantitative strategy research.'),
    },
    {
      name: 'Andy',
      role: t('about.team.members.2.role', 'Risk Control Director'),
      bio: t('about.team.members.2.bio', 'Former JP Morgan risk management expert, skilled in quantitative risk models and market risk control.'),
    },
    {
      name: 'Cain',
      role: t('about.team.members.3.role', 'Operations Director'),
      bio: t('about.team.members.3.bio', 'Rich experience in cryptocurrency exchange operations, focusing on market strategy and business development.'),
    },
  ];
  
  // Try to get the team from translations, fall back to default if it's not an array
  const teamFromTranslation = t('about.team.members', { returnObjects: true });
  const team = Array.isArray(teamFromTranslation) ? teamFromTranslation : defaultTeam;

  return (
    <>
      <Helmet>
        <title>{t('about.title', 'About Us')} - 9527.fi</title>
        <meta name="description" content={t('about.background.content', '9527.fi was founded in 2018 as a professional institution focused on cryptocurrency market making services. We have an experienced team dedicated to providing stable and reliable liquidity solutions for exchanges and projects.')} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary to-secondary text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {t('about.title', 'About 9527.fi')}
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {t('about.subtitle', 'Founded in 2018, committed to providing professional liquidity solutions for cryptocurrency markets')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold mb-6">{t('about.background.title', 'Company Background')}</h2>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="prose prose-lg max-w-none"
              >
                <p>
                  {t('about.background.content', '9527.fi was founded in 2018 as a professional institution focused on cryptocurrency market making services. We have an experienced team dedicated to providing stable and reliable liquidity solutions for exchanges and projects.')}
                </p>
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://placehold.co/800x600/1E293B/ffffff?text=9527.fi+Office"
                alt="9527.fi Office"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('about.team.title', 'Core Team')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name + index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <img
                  src={`https://placehold.co/400x500/${index === 0 ? '1E40AF' : index === 1 ? '1E293B' : index === 2 ? '3B82F6' : '1E40AF'}/ffffff?text=${member.name}`}
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{member.name}</h3>
                  <p className="text-primary font-medium mb-2">{member.role}</p>
                  <p className="text-gray-600">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">{t('about.vision.title', 'Our Vision')}</h2>
            <p className="text-xl text-gray-600">
              {t('about.vision.content', 'To become the most trusted crypto asset market maker globally, promoting the healthy development of the crypto asset market through technological innovation and professional services, creating long-term value for investors.')}
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default About; 